import { inject, injectable } from '@servicetitan/react-ioc';
import { ApiException, SettingsApi } from 'api';
import { makeObservable, observable, runInAction } from 'mobx';
import { CustomerPortalInvoiceFilter } from '../api/portal-invoices.api';

@injectable()
export class InvoicesSettingsFiltersStore {
    @observable settingsFilterFlags: CustomerPortalInvoiceFilter = 0;

    private loading = false;

    constructor(@inject(SettingsApi) private settingsApi: SettingsApi) {
        makeObservable(this);
    }

    async init() {
        if (this.loading) {
            return null;
        }

        this.loading = true;

        const settingsFilterFlags = await this.loadInvoicesSettingsFilters();

        this.loading = false;
        runInAction(() => {
            this.settingsFilterFlags = settingsFilterFlags;
        });
        return this.settingsFilterFlags;
    }

    loadInvoicesSettingsFilters = async (): Promise<CustomerPortalInvoiceFilter> => {
        try {
            const rawInvoiceFilter = await this.getRawInvoiceFilterOrEmpty();

            const invoiceFilter = {
                displayPendingInvoices: !!(rawInvoiceFilter.displayPendingInvoices ?? true),
                displayPostedInvoices: !!(rawInvoiceFilter.displayPostedInvoices ?? true),
                displayExportedInvoices: !!(rawInvoiceFilter.displayExportedInvoices ?? true),
                displayInvoicesNeedingReview: !!(
                    rawInvoiceFilter.displayInvoicesNeedingReview ?? true
                ),
                displayInvoicesWithReviewOnHold: !!(
                    rawInvoiceFilter.displayInvoicesWithReviewOnHold ?? true
                ),
                displayReviewedInvoices: !!(rawInvoiceFilter.displayReviewedInvoices ?? true),
            };

            const invoiceFilterFlags =
                (invoiceFilter.displayPendingInvoices ? 1 << 0 : 0) |
                (invoiceFilter.displayPostedInvoices ? 1 << 1 : 0) |
                (invoiceFilter.displayExportedInvoices ? 1 << 2 : 0) |
                (invoiceFilter.displayInvoicesNeedingReview ? 1 << 3 : 0) |
                (invoiceFilter.displayInvoicesWithReviewOnHold ? 1 << 4 : 0) |
                (invoiceFilter.displayReviewedInvoices ? 1 << 5 : 0);

            return invoiceFilterFlags;
        } catch {
            return 0;
        }
    };

    private async getRawInvoiceFilterOrEmpty() {
        try {
            const result = await this.settingsApi.client.getSpecialSettings('invoice-filter');
            return (typeof result === 'object' ? result : {}) ?? {};
        } catch (ex) {
            if (ex instanceof ApiException && ex.status === 404) {
                return {};
            }

            throw ex;
        }
    }
}
